import React from 'react';
import { motion } from 'framer-motion';
import { Monitor, Shield, FileText, Lock, Users, Activity, HeartPulse, Briefcase, ArrowUpRight } from 'lucide-react';
import { GlassCard } from './GlassCard';
import { SectionHeading } from './SectionHeading';
import { cn } from '../utils/cn';
const programs = [{
  title: 'Computer 101',
  desc: 'Foundational computing and networking essentials for security professionals.',
  icon: Monitor,
  level: 'Beginner',
  duration: '4 Weeks',
  path: 'IT Fundamentals',
  color: 'text-blue-400'
}, {
  title: 'Cybersecurity for Beginners',
  desc: 'Introduction to the threat landscape, defense mechanisms, and security operations.',
  icon: Shield,
  level: 'Beginner',
  duration: '8 Weeks',
  path: 'Security Analyst',
  color: 'text-primary-blue'
}, {
  title: 'Third-Party Risk Management',
  desc: 'Master the art of assessing and managing risks from vendors and partners.',
  icon: Users,
  level: 'Intermediate',
  duration: '6 Weeks',
  path: 'Risk Manager',
  color: 'text-accent-cyan'
}, {
  title: 'Corporate Compliance',
  desc: 'Navigate regulatory requirements and implement effective compliance programs.',
  icon: FileText,
  level: 'Advanced',
  duration: '10 Weeks',
  path: 'Compliance Officer',
  color: 'text-status-success'
}, {
  title: 'Data Privacy & Protection',
  desc: 'Deep dive into GDPR, CCPA, and global data protection frameworks.',
  icon: Lock,
  level: 'Intermediate',
  duration: '8 Weeks',
  path: 'Privacy Specialist',
  color: 'text-accent-blue'
}, {
  title: 'Executive Protection',
  desc: 'Specialized security training for high-net-worth individuals and executives.',
  icon: Briefcase,
  level: 'Advanced',
  duration: '12 Weeks',
  path: 'Security Director',
  color: 'text-status-warning'
}, {
  title: 'Cyber Security Resilience',
  desc: 'Build systems that can withstand and recover from sophisticated cyber attacks.',
  icon: Activity,
  level: 'Advanced',
  duration: '10 Weeks',
  path: 'Resilience Engineer',
  color: 'text-status-danger'
}, {
  title: 'Governance, Risk & GRC',
  desc: 'The ultimate track for mastering enterprise security governance and risk.',
  icon: Shield,
  level: 'Advanced',
  duration: '14 Weeks',
  path: 'GRC Lead',
  color: 'text-white'
}, {
  title: 'Healthcare Compliance',
  desc: 'Navigate HIPAA, HITECH, and specialized healthcare security regulations.',
  icon: HeartPulse,
  level: 'Intermediate',
  duration: '8 Weeks',
  path: 'Healthcare Security',
  color: 'text-pink-500'
}];
export function ProgramsSection() {
  return <section className="py-24 bg-background-secondary/50" data-id="element-283">
      <div className="container mx-auto px-6" data-id="element-284">
        <SectionHeading title="Industry-Leading Training Programs" subtitle="Our comprehensive curriculum is designed by industry veterans to prepare you for the most demanding roles in cybersecurity." data-id="element-285" />

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-286">
          {programs.map((program, i) => <motion.div key={program.title} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.05
        }} data-id="element-287">
              <GlassCard className="p-8 group h-full flex flex-col border-white/5 hover:border-primary-blue/30 transition-all duration-500" data-id="element-288">
                <div className="flex justify-between items-start mb-6" data-id="element-289">
                  <div className={cn('p-3 rounded-2xl bg-white/5 group-hover:scale-110 transition-transform duration-500', program.color)} data-id="element-290">
                    <program.icon size={28} data-id="element-291" />
                  </div>
                  <div className="flex flex-col items-end gap-1" data-id="element-292">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-white/30" data-id="element-293">
                      Level
                    </span>
                    <span className={cn('text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border', program.level === 'Beginner' ? 'text-status-success border-status-success/20 bg-status-success/5' : program.level === 'Intermediate' ? 'text-status-warning border-status-warning/20 bg-status-warning/5' : 'text-status-danger border-status-danger/20 bg-status-danger/5')} data-id="element-294">
                      {program.level}
                    </span>
                  </div>
                </div>

                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-primary-blue transition-colors" data-id="element-295">
                  {program.title}
                </h3>
                <p className="text-sm text-white/50 leading-relaxed mb-6 flex-grow" data-id="element-296">
                  {program.desc}
                </p>

                <div className="space-y-4 pt-6 border-t border-white/5" data-id="element-297">
                  <div className="flex justify-between text-xs" data-id="element-298">
                    <span className="text-white/30 uppercase font-bold tracking-wider" data-id="element-299">
                      Pathway
                    </span>
                    <span className="text-white/70 font-medium" data-id="element-300">
                      {program.path}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs" data-id="element-301">
                    <span className="text-white/30 uppercase font-bold tracking-wider" data-id="element-302">
                      Duration
                    </span>
                    <span className="text-white/70 font-medium" data-id="element-303">
                      {program.duration}
                    </span>
                  </div>
                </div>

                <button className="mt-8 w-full py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm font-bold flex items-center justify-center gap-2 group-hover:bg-primary-blue group-hover:border-primary-blue transition-all" data-id="element-304">
                  View Curriculum <ArrowUpRight size={16} data-id="element-305" />
                </button>
              </GlassCard>
            </motion.div>)}
        </div>
      </div>
    </section>;
}